import React from 'react';
import {ImageSourcePropType, View} from 'react-native';

import Screen from '../../shared/components/screen';

import {Props} from './model';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Routes, Screens} from '../../shared/routing';
import {useStyles} from '../../shared/hooks/styles';
import {DeviceSize, useDeviceSize} from '../../shared/theme/helpers';
import {ResponsiveView} from '../../shared/components/responsiveView';
import {Hr} from '../../shared/components/hr';
import {ContactsCard} from '../../shared/components/contactsCard';
import {ContentWithRightImage} from '../../shared/components/contentWithRightImage';
import {Button} from '../../shared/components/button';
import {Text} from '../../shared/components/text';
import {Image} from '../../shared/components/image';
import {Markdown} from '../../shared/components/markdown';
import {images} from '../../assets/icons';
import {Banking} from '../../assets/images';
import {formatPhone, formatUrl, openExternalLink, openExternalPhone} from '../../shared/pipes';
import {CustomScrollView} from '../../shared/components/scrollView';
import {VisImpStatus, VisImpSwitchColor} from '../../shared/models/visuallyImpairedSettings';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../redux/selectors/runtime/theme';

const routes = [
  {
    title: 'Возможности карты',
    routeName: Screens.CardServices,
    webRoute: Routes.cardService,
  },
  {
    title: 'Банковские сервисы',
    routeName: Screens.TransportService,
    webRoute: Routes.transportService,
  },
];

export default ({content, onPressUrl}: Props) => {
  const deviceSize = useDeviceSize();
  const visImpState = useSelector(themeSelector());
  const styles = useStyles(
    ({mediaOnly, theme}) => {
      return {
        screen: {
          backgroundColor: theme.screen.container[deviceSize].backgroundColor,
        },
        sectionTitle: {
          marginTop: 38,
          marginBottom: 5,
        },
        contentTextWrapper: mediaOnly(DeviceSize.desktop, {marginBottom: 0}, {marginBottom: 30}),
        sectionContainer: {
          overflow: 'hidden',
          ...mediaOnly(
            DeviceSize.desktop,
            {marginTop: 30, paddingHorizontal: 24},
            {marginTop: 18, paddingHorizontal: 16},
          ),
        },
        contentWithRightImageIcon: {
          marginTop: -70,
        },
        contentWithRightImageContainer: mediaOnly(DeviceSize.desktop, {paddingBottom: 60}, {paddingBottom: 47}),
        contentWidthRightImageBackground: mediaOnly(DeviceSize.desktop, {}, {right: -24, bottom: -74}),
        featuresList: {},
        sectionDescription: {
          marginTop: 8,
        },
        contacts: {
          marginBottom: 30,
          ...mediaOnly(DeviceSize.desktop, {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }),
        },
        contactsCard: {
          marginRight: 24,
        },
        contactsImage: {
          backgroundColor:
            visImpState?.visuallyImpaired === VisImpStatus.Active &&
            visImpState?.switchColor === VisImpSwitchColor.Invert
              ? theme.color.black
              : theme.color.white,
        },
        openBankButton: {
          flexShrink: 1,
          ...mediaOnly(DeviceSize.desktop, {width: 309, marginLeft: 'auto'}),
        },
        bankLinkText: {
          color: theme.color.primaryColor,
        },
      };
    },
    [visImpState?.visuallyImpaired, visImpState?.switchColor],
  );

  const openBankButton = content.services.bankingService.bankEntryLink ? (
    <View style={styles.openBankButton}>
      <Button
        id='bank-lk'
        onPress={() => openExternalLink(content.services.bankingService.bankEntryLink)}
        block={true}
        type='primary'
        text='Войти в личный кабинет банка'
      />
    </View>
  ) : null;

  return (
    <Screen style={styles.screen}>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
        <View style={styles.sectionContainer}>
          <Breadcrumbs routes={routes} />
          <ResponsiveView only={[DeviceSize.desktop]}>
            <Text testID='banking-service-label-id' type='h2' style={styles.sectionTitle}>
              Банковские сервисы
            </Text>
          </ResponsiveView>
          <ContentWithRightImage
            containerStyle={styles.contentWithRightImageContainer}
            iconStyle={styles.contentWithRightImageIcon}
            icon={<Image source={Banking as any} width={633} height={635} />}
            backgroundStyle={styles.contentWidthRightImageBackground}>
            <View style={styles.contentTextWrapper}>
              <Markdown listColumns={deviceSize === DeviceSize.desktop ? 2 : 1}>
                {content.services.bankingService.descriptionMarkdown}
              </Markdown>
            </View>
            <Hr
              marginBottom={deviceSize === DeviceSize.desktop ? 27 : 16}
              marginTop={deviceSize === DeviceSize.desktop ? 30 : 0}
            />
            <View style={styles.contacts}>
              <ContactsCard
                style={styles.contactsCard}
                contacts={[
                  {
                    icon: images.Phone, // 8 800 100 00 06
                    value: formatPhone(content.services.bankingService.bankPhone),
                    onPress: () => {
                      openExternalPhone(content.services.bankingService.bankPhone);
                    },
                  },
                  {
                    icon: images.Globe,
                    value: formatUrl(content.services.bankingService.bankWebsite),
                    onPress: () => {
                      openExternalLink(content.services.bankingService.bankWebsite);
                    },
                    textStyle: styles.bankLinkText,
                  },
                ]}
                imageProps={{
                  source:
                    typeof content.services.bankingService.logoUrl === 'string'
                      ? {uri: content.services.bankingService.logoUrl}
                      : (content.services.bankingService.logoUrl as unknown as ImageSourcePropType),
                  width: deviceSize === DeviceSize.desktop ? 174 : undefined,
                  height: 94,
                }}
                imageWrapperStyles={styles.contactsImage}
              />
              {deviceSize === DeviceSize.desktop && openBankButton}
            </View>
            {deviceSize !== DeviceSize.desktop && openBankButton}
          </ContentWithRightImage>
        </View>
      </CustomScrollView>
    </Screen>
  );
};
